/**
 * Authentication Form Validation Utilities
 * Centralized validation for all auth forms
 */

// Validation regex patterns
export const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // At least 8 characters, one upper, one lower, one number, one special character
  // Allows letters, numbers, and common special characters (no whitespace)
  password:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`])[\S]{8,}$/,
  name: /^[a-zA-Z\s]{2,50}$/,
};

// Validation error messages
export const VALIDATION_MESSAGES = {
  required: () => `Required field`,
  invalidEmail: "Please enter a valid email.",
  invalidPassword:
    "At least 8 characters, one upper and lower character, one number and one symbol.",
  invalidName: "Name must be 2-50 characters and contain only letters.",
  passwordMismatch: "Passwords do not match.",
  weakPassword: "Password is too weak. Please choose a stronger password.",
};

/**
 * Validate individual field
 * @param {string} value - Field value
 * @param {string} type - Field type (email, password, name, required)
 * @param {string} fieldName - Display name for error message
 * @returns {string} Error message or empty string
 */
export const validateField = (value, type, fieldName = "Field") => {
  // Check if required field is empty
  if (type === "required" && (!value || !value.trim())) {
    return VALIDATION_MESSAGES.required(fieldName);
  }

  // Skip other validations if value is empty (not required)
  if (!value || !value.trim()) {
    return "";
  }

  switch (type) {
    case "email":
      return VALIDATION_PATTERNS.email.test(value)
        ? ""
        : VALIDATION_MESSAGES.invalidEmail;

    case "password":
      return VALIDATION_PATTERNS.password.test(value)
        ? ""
        : VALIDATION_MESSAGES.invalidPassword;

    case "name":
      return VALIDATION_PATTERNS.name.test(value)
        ? ""
        : VALIDATION_MESSAGES.invalidName;

    default:
      return "";
  }
};

/**
 * Validate entire form based on rules
 * @param {Object} formData - Form data object
 * @param {Object} validationRules - Validation rules for each field
 * @returns {Object} Object with field errors
 */
export const validateForm = (formData, validationRules) => {
  const errors = {};

  Object.keys(validationRules).forEach((fieldName) => {
    const rules = validationRules[fieldName];
    const value = formData[fieldName];

    // Check each rule for the field
    rules.forEach((rule) => {
      if (!errors[fieldName]) {
        // Only set first error found
        const error = validateField(value, rule.type, rule.label || fieldName);
        if (error) {
          errors[fieldName] = error;
        }
      }
    });
  });

  return errors;
};

/**
 * Validation rules for different auth forms
 */
export const AUTH_VALIDATION_RULES = {
  signin: {
    email: [{ type: "required", label: "Email" }, { type: "email" }],
    password: [{ type: "required", label: "Password" }],
  },

  signup: {
    firstName: [{ type: "required", label: "First name" }],
    lastName: [{ type: "required", label: "Last name" }],
    email: [{ type: "required", label: "Email" }, { type: "email" }],
    password: [{ type: "required", label: "Password" }, { type: "password" }],
  },

  forgotPassword: {
    email: [{ type: "required", label: "Email" }, { type: "email" }],
  },

  createPassword: {
    password: [{ type: "required", label: "Password" }, { type: "password" }],
  },
};

/**
 * Get validation rules for specific form
 * @param {string} formType - Type of form (signin, signup, etc.)
 * @returns {Object} Validation rules object
 */
export const getValidationRules = (formType) => {
  return AUTH_VALIDATION_RULES[formType] || {};
};

/**
 * Validate single auth form
 * @param {Object} formData - Form data
 * @param {string} formType - Form type
 * @returns {Object} Validation errors
 */
export const validateAuthForm = (formData, formType) => {
  const rules = getValidationRules(formType);
  return validateForm(formData, rules);
};
